import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Row, Col, Card, CardHeader, CardBody, Button, Input } from "reactstrap";
import { Catalogue, Product } from "../../../types/catalogue.types";
import { useDispatch } from "react-redux";
import { getProductsByCatAlogueId, deleteProductInCatAlogue } from "../../../Store/catelogue/actions";
import { getProducts, updateProducts } from "../../../Store/products/actions";
import notifications from "../../../common/Components/Notifications/Notification";
import Pagination from "@mui/material/Pagination";
import { PageJumpControl } from "../../../common/Components/Index";
import { usePagination } from "../../../hooks/usePagination";
import {
  loadCatalogueCommerceRollup,
  signalForProductRow,
  type ProductCommerceSignal,
} from "../catalogueShopperSignals";
import {
  CATALOGUE_PRICE_PRESETS,
  PRICE_SAFETY_PCT,
  buildPricingUpsertEntries,
  changePctVsDefault,
  effectiveClientTier,
  fetchCataloguePricingSheet,
  inputsFromSheet,
  parseRowEffective,
  saveCataloguePricing,
  type CataloguePriceTier,
  type PricingSheetEntry,
} from "../cataloguePricing";
import { isProductInStock } from "../../../pages/products/productStockDisplay";
import { isSystemManagedCatalogue } from "../catalogueSystemManaged";
import { applyDraftOverlay, publishProductDraft } from "../../../uploads/productDraftApi";
import { isProductDraftRevisionsEnabled } from "../../../uploads/productDraftRevisionsEnabled";
import HKPConfirmDialog from "../../../common/Components/HKPConfirmDialog";
import HKPDrawer from "../../../common/Components/HKPDrawer";
import { catalogueMembershipId, catalogueRemovalError, catalogueRemovalSuccess, confirmedCatalogueRemoval } from "../catalogueRemoval";

interface ProductsTabProps {
  catalogue: Catalogue;
  onAddFromInventory?: () => void;
  priceTier: CataloguePriceTier;
  onPriceTierChange: (next: CataloguePriceTier) => void;
}

const toNumberOrNull = (value: any): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const resolveProductThumbnail = (product: any): string | undefined => {
  const mediaCandidates = [
    product?.thumbnailDetails?.media?.[0]?.mediaURL,
    product?.thumbnailDetails?.media?.[0]?.url,
    product?.thumbnail?.mediaURL,
    product?.thumbnail?.url,
    product?.images?.[0]?.mediaURL,
    product?.images?.[0]?.url,
    product?.media?.[0]?.mediaURL,
    product?.media?.[0]?.url,
    product?.image?.mediaURL,
    product?.image?.url,
    product?.imageUrl,
  ];

  return mediaCandidates.find((value) => typeof value === "string" && value.trim().length > 0);
};

const resolvePriceValue = (product: any): number => {
  const price = toNumberOrNull(product?.price);
  const nestedPrice = toNumberOrNull(product?.price?.price) ?? toNumberOrNull(product?.price?.amount);
  const basePrice = toNumberOrNull(product?.basePrice);
  const nestedBasePrice = toNumberOrNull(product?.basePrice?.basePrice) ?? toNumberOrNull(product?.basePrice?.amount);
  return price ?? nestedPrice ?? basePrice ?? nestedBasePrice ?? 0;
};

const resolveBasePriceValue = (product: any): number => {
  const basePrice = toNumberOrNull(product?.basePrice);
  const nestedBasePrice = toNumberOrNull(product?.basePrice?.basePrice) ?? toNumberOrNull(product?.basePrice?.amount);
  const price = toNumberOrNull(product?.price);
  const nestedPrice = toNumberOrNull(product?.price?.price) ?? toNumberOrNull(product?.price?.amount);
  return basePrice ?? nestedBasePrice ?? price ?? nestedPrice ?? 0;
};

const resolveStockValue = (product: any): number => {
  return isProductInStock(product) ? 1 : 0;
};

const resolveProductStatus = (product: any): "in_stock" | "out_of_stock" | "low_stock" => {
  return isProductInStock(product) ? "in_stock" : "out_of_stock";
};

const ProductsTab: React.FC<ProductsTabProps> = ({
  catalogue,
  onAddFromInventory,
  priceTier,
  onPriceTierChange,
}) => {
  const dispatch = useDispatch();
  const stockLocked = isSystemManagedCatalogue(catalogue);
  
  // SOLID Pagination hooks
  const catalogueProductsPagination = usePagination({ initialLimit: 10 });
  
  // Product management states
  const [products, setProducts] = useState<Product[]>([]);
  const [productsError, setProductsError] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<Product | null>(null);
  const [removing, setRemoving] = useState(false);
  const removalInFlight = useRef(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showViewModal, setShowViewModal] = useState<boolean>(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [editStockValue, setEditStockValue] = useState<number>(0);
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const [draftPendingPublishId, setDraftPendingPublishId] = useState<string>("");
  const [commerceByProduct, setCommerceByProduct] = useState<
    Record<string, ProductCommerceSignal>
  >({});
  const [commerceLoading, setCommerceLoading] = useState(false);
  const [pricingEntries, setPricingEntries] = useState<PricingSheetEntry[]>([]);
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  const [pricingLoading, setPricingLoading] = useState(false);
  const [savingPriceId, setSavingPriceId] = useState<string | null>(null);
  const [savingVisiblePrices, setSavingVisiblePrices] = useState(false);

  const clientTier = effectiveClientTier(priceTier);
  const pricingById = useMemo(() => {
    const map: Record<string, PricingSheetEntry> = {};
    pricingEntries.forEach((entry) => {
      if (entry.productUniqueId) map[entry.productUniqueId] = entry;
    });
    return map;
  }, [pricingEntries]);

  const loadPricing = useCallback(async () => {
    if (!catalogue.id) return;
    setPricingLoading(true);
    try {
      const sheet = await fetchCataloguePricingSheet(catalogue.id, clientTier);
      setPricingEntries(sheet.entries);
      setPriceInputs(inputsFromSheet(sheet.entries));
    } catch (error) {
      notifications({
        title: "Pricing",
        message: "We couldn't load catalogue pricing. Refresh the catalogue and try again.",
        type: "danger",
      });
      setPricingEntries([]);
      setPriceInputs({});
    } finally {
      setPricingLoading(false);
    }
  }, [catalogue.id, clientTier]);

  // Load products from API when component mounts
  useEffect(() => {
    if (catalogue.id) {
      loadProducts();
    }
  }, [catalogue.id]);

  useEffect(() => {
    void loadPricing();
  }, [loadPricing]);

  useEffect(() => {
    if (!catalogue.id) return;
    let cancelled = false;
    (async () => {
      setCommerceLoading(true);
      try {
        const { byProductId } = await loadCatalogueCommerceRollup(catalogue.id);
        if (!cancelled) setCommerceByProduct(byProductId);
      } catch {
        if (!cancelled) setCommerceByProduct({});
      } finally {
        if (!cancelled) setCommerceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogue.id]);

  // Load products from API. pageOffset is 0-based page index (Index / catalogue products API).
  const loadProducts = (pageOffset?: number) => {
    catalogueProductsPagination.setLoading(true);
    const offset =
      pageOffset !== undefined ? pageOffset : catalogueProductsPagination.pagination.offset;
    
    dispatch(getProductsByCatAlogueId(
      { 
        offset, 
        limit: catalogueProductsPagination.pagination.limit, 
        search: searchTerm, 
        sortBy: "name", 
        sortOrder: "asc" 
      }, 
      catalogue.id, 
      (response: any) => {
        catalogueProductsPagination.setLoading(false);
        if (response?.success && response.data) {
          setProductsError(false);
          // Update pagination from response
          catalogueProductsPagination.updatePaginationFromResponse(response, offset);
          
          // Transform API response to Product interface
          const apiProducts = response.data.products?.map((product: any) => {
            const stockValue = resolveStockValue(product);
            const thumbnailUrl = resolveProductThumbnail(product);

            return {
              id: catalogueMembershipId(product),
              name: typeof product.name === 'string' ? product.name : (product.name?.name || product.name?._id || "Not Available"),
              price: resolvePriceValue(product),
              basePrice: resolveBasePriceValue(product),
              stock: stockValue,
              status: resolveProductStatus(product),
              category: typeof product.category === 'string' ? product.category : (product.category?.name || product.category?._id || product.category || "Not Available"),
              sku: typeof product.sku === 'string' ? product.sku : (product.sku?.sku || product.sku || product.productUniqueId || "N/A"),
              images: thumbnailUrl ? [thumbnailUrl] : [],
              backendId: product?._id || product?.id || null
            } as Product & { backendId?: string | null };
          }) || [];
          setProducts(apiProducts);
        } else {
          setProductsError(true);
        }
      }
    ));
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusBadge = (status: string) => {
    let badgeClass = "bg-success";
    let text = "IN STOCK";
    
    if (status === "out_of_stock") {
      badgeClass = "bg-danger";
      text = "OUT OF STOCK";
    } else if (status === "low_stock") {
      badgeClass = "bg-warning";
      text = "LOW STOCK";
    }
    
    return (
      <span className={`badge ${badgeClass} text-white`} style={{ fontSize: "10px" }}>
        {text}
      </span>
    );
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setEditStockValue(typeof product.stock === "number" ? product.stock : 0);
    setShowEditModal(true);
  };

  const handleViewProduct = (product: Product) => {
    setViewingProduct(product);
    setShowViewModal(true);
  };

  const setCataloguePriceInput = (productUniqueId: string, value: string) => {
    setPriceInputs((prev) => ({ ...prev, [productUniqueId]: value }));
  };

  const saveCataloguePrices = async (rows: PricingSheetEntry[], busyId: string | null) => {
    if (!catalogue.id || rows.length === 0) return;
    if (busyId) setSavingPriceId(busyId);
    else setSavingVisiblePrices(true);
    try {
      const payload = buildPricingUpsertEntries(rows, priceInputs);
      const savedTier = await saveCataloguePricing(catalogue.id, clientTier, payload);
      notifications({
        title: "Saved",
        message:
          rows.length === 1
            ? `Catalogue price updated for ${rows[0].name} (${savedTier}).`
            : `Catalogue prices updated for ${rows.length} products (${savedTier}).`,
        type: "success",
      });
      await loadPricing();
    } catch (error) {
      notifications({
        title: "Pricing",
        message: "We couldn't save catalogue pricing. Your prices haven't been changed. Try again.",
        type: "danger",
      });
    } finally {
      setSavingPriceId(null);
      setSavingVisiblePrices(false);
    }
  };

  const handleDeleteProduct = () => {
    const id = pendingRemoval?.id;
    if (removalInFlight.current || !id) return;
    removalInFlight.current = true;
    setRemoving(true);
    dispatch(deleteProductInCatAlogue({ productIds: [id] }, catalogue.id, (response: any) => {
      removalInFlight.current = false;
      setRemoving(false);
      const result = confirmedCatalogueRemoval(response, [id]);
      if (result) {
        setPendingRemoval(null);
        setProducts(current => current.filter(product => !result.affectedIds.includes(product.id)));
        notifications({ title: "Product removed", message: catalogueRemovalSuccess(result), type: "success" });
        const offset = catalogueProductsPagination.pagination.offset;
        const limit = catalogueProductsPagination.pagination.limit;
        loadProducts(offset > 0 && offset * limit >= result.remainingCount ? offset - 1 : offset);
        void loadPricing();
      } else {
        setPendingRemoval(null);
        notifications({ title: "Product not removed", message: catalogueRemovalError(response), type: "danger", duration: 6000 });
      }
    }));
  };

  const handleSaveProductEdit = () => {
    if (!editingProduct) {
      return;
    }

    const backendId = (editingProduct as Product & { backendId?: string | null })?.backendId;
    const productLookupId = backendId || editingProduct.id;
    if (!productLookupId) {
      notifications({
        title: "Error!",
        message: "Product ID not found for update. Please refresh and try again.",
        type: "danger",
      });
      return;
    }

    setIsSavingEdit(true);
    dispatch(
      getProducts(
        { offset: 0, limit: 1, ProductId: productLookupId },
        (detailResponse: any) => {
          const fullProduct = applyDraftOverlay(detailResponse?.data?.docs?.[0]);
          if (!detailResponse?.success || !fullProduct) {
            setIsSavingEdit(false);
            notifications({
              title: "Product not updated",
              message: "We couldn't load the latest product details. Your changes weren't saved. Try again.",
              type: "danger",
            });
            return;
          }

          const normalizedStock = Number.isFinite(editStockValue) ? Math.max(0, editStockValue) : 0;
          const toMediaItems = (mediaList: any[] = []) =>
            mediaList
              .map((item: any) => ({
                mediaUrl: item?.mediaUrl || item?.mediaURL || item?.url || "",
                show: typeof item?.show === "number" ? item.show : 0,
                pin: item?.pin,
              }))
              .filter((item: any) => item.mediaUrl);

          const toMediaBlock = (node: any) => ({
            media: toMediaItems(node?.media || []),
            karigarId: node?.karigar?._id || node?.karigarId || "",
          });

          const payload = {
            id: fullProduct?._id,
            srNo: fullProduct?.srNo || "",
            designNum: fullProduct?.designNum || "",
            name: String(fullProduct?.name || editingProduct.name || "").trim(),
            xuid: fullProduct?.xuid || "",
            tags: fullProduct?.tags || [],
            thumbnails: {
              media: toMediaItems(fullProduct?.thumbnailDetails?.media || []),
            },
            artformId: fullProduct?.artform?._id || "",
            typeId: fullProduct?.type?._id || "",
            categoryId: fullProduct?.category?._id || "",
            subcategoryId: fullProduct?.subcategory?._id || "",
            weavetypeId: fullProduct?.weavetype?._id || "",
            designing: toMediaBlock(fullProduct?.designingDetails),
            weaving: toMediaBlock(fullProduct?.weavingDetails),
            dying: toMediaBlock(fullProduct?.dyingDetails),
            finishing: toMediaBlock(fullProduct?.finishingDetails),
            status: fullProduct?.status === "COMPLETED" ? 1 : 0,
            inStock: normalizedStock > 0,
            color: fullProduct?.color || "",
            additionalImages: fullProduct?.additionalImages || [],
            brand: fullProduct?.brand || "",
            weaver: fullProduct?.weaver || "",
            handloom: fullProduct?.handloom || "",
            silk: fullProduct?.silk || "",
            zari: fullProduct?.zari || "",
            sareeWeight: fullProduct?.sareeWeight || "",
            zariWeight: fullProduct?.zariWeight || "",
            purity: fullProduct?.purity || "",
            madeIn: fullProduct?.madeIn || "",
            yarnType: fullProduct?.yarnType || "",
            workingHours: fullProduct?.workingHours || "",
            loomNum: fullProduct?.loomNum || "",
            weavingCenter: fullProduct?.weavingCenter || "",
            typeOfSareeByDesign: fullProduct?.typeOfSareeByDesign || "",
            productionTimeInMonths: fullProduct?.productionTimeInMonths || "",
            sellPrice: fullProduct?.sellPrice || "",
            costPrice: fullProduct?.costPrice || "",
          };

          dispatch(
            updateProducts(payload, (updateResponse: any) => {
              setIsSavingEdit(false);
              if (updateResponse?.success) {
                const savedAsDraft =
                  isProductDraftRevisionsEnabled() && fullProduct?.status === "COMPLETED";
                notifications({
                  title: "Success!",
                  message: savedAsDraft
                    ? "Draft changes saved. Publish to show them on QR/NFC and public catalogue."
                    : "Product updated successfully",
                  type: "success",
                });
                if (savedAsDraft) {
                  setDraftPendingPublishId(String(fullProduct?._id || ""));
                  loadProducts();
                  return;
                }
                setShowEditModal(false);
                setEditingProduct(null);
                loadProducts();
              } else {
                notifications({
                  title: "Product not updated",
                  message: "We couldn't update this product. Your changes weren't saved. Try again.",
                  type: "danger",
                });
              }
            })
          );
        }
      )
    );
  };

  const handleAddFromInventory = () => {
    if (onAddFromInventory) {
      onAddFromInventory();
      return;
    }
    notifications({
      title: "Add from inventory",
      message: "Could not open Add from inventory for this catalogue.",
      type: "warning",
    });
  };

  const categories = [
    "all",
    ...Array.from(new Set(products.map((p) => String(p.category || "Not Available")))),
  ];

  const renderShopperInterest = (productId: string) => {
    const sig = signalForProductRow(commerceByProduct, productId);
    if (commerceLoading) {
      return <span className="text-muted small">…</span>;
    }
    if (!sig) {
      return <span className="text-muted small">—</span>;
    }
    const hasDraft =
      sig.draftShortlistVisitors > 0 || sig.draftCartVisitors > 0;
    const hasFinal =
      sig.shortlistSubmissions > 0 || sig.cartSubmissions > 0;
    if (!hasDraft && !hasFinal) {
      return <span className="text-muted small">—</span>;
    }
    return (
      <div className="d-flex flex-column gap-1 align-items-start">
        {hasDraft ? (
          <>
            <span
              className="text-uppercase text-muted"
              style={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.08em" }}
            >
              Live draft
            </span>
            {sig.draftShortlistVisitors > 0 ? (
              <span
                className="badge rounded-pill"
                title="Visitors have this on their shelf shortlist — not yet submitted to you"
                style={{
                  backgroundColor: "#eff6ff",
                  color: "#1e40af",
                  border: "1px solid #93c5fd",
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              >
                Shortlist · {sig.draftShortlistVisitors} visitor
                {sig.draftShortlistVisitors === 1 ? "" : "s"}
              </span>
            ) : null}
            {sig.draftCartVisitors > 0 ? (
              <span
                className="badge rounded-pill"
                title="Visitors have this in shelf cart — not yet submitted"
                style={{
                  backgroundColor: "#f5f3ff",
                  color: "#5b21b6",
                  border: "1px solid #c4b5fd",
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              >
                Cart · {sig.draftCartVisitors}v · {sig.draftCartUnits} pcs
              </span>
            ) : null}
          </>
        ) : null}
        {hasFinal ? (
          <>
            <span
              className="text-uppercase text-muted"
              style={{
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                marginTop: hasDraft ? "2px" : 0,
              }}
            >
              Submitted
            </span>
            {sig.shortlistSubmissions > 0 ? (
              <span
                className="badge rounded-pill"
                title="Final shortlist submissions to your studio"
                style={{
                  backgroundColor: "#fff7ed",
                  color: "#9a3412",
                  border: "1px solid #fdba74",
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              >
                Shortlisted ×{sig.shortlistSubmissions}
              </span>
            ) : null}
            {sig.cartSubmissions > 0 ? (
              <span
                className="badge rounded-pill"
                title="Final cart-intent submissions"
                style={{
                  backgroundColor: "#ecfdf5",
                  color: "#065f46",
                  border: "1px solid #6ee7b7",
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              >
                Cart ×{sig.cartSubmissions}
                {sig.cartUnits > sig.cartSubmissions
                  ? ` · ${sig.cartUnits} pcs`
                  : ""}
              </span>
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  const renderProductPrice = (product: Product) => {
    const pricing = pricingById[product.id];
    const defaultPrice =
      pricing?.productDefaultPrice ?? (typeof product.price === "number" ? product.price : 0);
    const fallbackRow: PricingSheetEntry = pricing || {
      productUniqueId: product.id,
      name: String(product.name || product.id),
      productDefaultPrice: defaultPrice,
      cataloguePrice: null,
      effectivePrice: defaultPrice,
      usesProductDefault: true,
      clientTier,
    };
    const nextPrice = parseRowEffective(fallbackRow, priceInputs);
    const diffPct = changePctVsDefault(defaultPrice, nextPrice);
    const isRisky = Math.abs(diffPct) >= PRICE_SAFETY_PCT;
    return (
      <div className="catalogue-product-price">
        <div className="catalogue-product-price-default">
          Product default ₹{defaultPrice > 0 ? defaultPrice.toLocaleString("en-IN") : "—"}
        </div>
        <div className="d-flex align-items-center gap-1 flex-wrap">
          <Input
            type="text"
            inputMode="decimal"
            className="catalogue-product-price-input"
            placeholder={defaultPrice > 0 ? String(defaultPrice) : "Default"}
            value={priceInputs[product.id] ?? ""}
            onChange={(e) => setCataloguePriceInput(product.id, e.target.value)}
            disabled={!pricing || pricingLoading}
          />
          <Button
            type="button"
            size="sm"
            outline
            className="catalogue-row-mini-btn"
            disabled={!pricing || (priceInputs[product.id] ?? "").trim() === "" || savingPriceId === product.id}
            onClick={() => setCataloguePriceInput(product.id, "")}
          >
            Default
          </Button>
          <Button
            type="button"
            size="sm"
            className="catalogue-row-mini-btn catalogue-row-mini-btn-solid"
            disabled={!pricing || savingPriceId === product.id}
            onClick={() => void saveCataloguePrices([fallbackRow], product.id)}
          >
            {savingPriceId === product.id ? "…" : "Save"}
          </Button>
        </div>
        <div className={`catalogue-product-price-delta${isRisky ? " is-risky" : ""}`}>
          {pricingLoading
            ? "Loading…"
            : !pricing
              ? "Price sheet unavailable"
              : `${diffPct >= 0 ? "+" : ""}${diffPct.toFixed(1)}% vs default`}
        </div>
      </div>
    );
  };

  const renderProductActions = (product: Product) => (
    <div className="d-flex gap-1 flex-wrap">
      <Button
        size="sm"
        outline
        style={{
          borderColor: "#17a2b8",
          color: "#17a2b8",
          fontSize: "11px",
          padding: "4px 8px",
        }}
        onClick={() => handleViewProduct(product)}
      >
        View
      </Button>
      <Button
        size="sm"
        outline
        style={{
          borderColor: "#0071E3",
          color: "#0071E3",
          fontSize: "11px",
          padding: "4px 8px",
        }}
        onClick={() => handleEditProduct(product)}
      >
        Edit
      </Button>
      {!stockLocked ? (
        <Button
          size="sm"
          outline
          style={{
            borderColor: "#dc3545",
            color: "#dc3545",
            fontSize: "11px",
            padding: "4px 8px",
          }}
          onClick={() => setPendingRemoval(product)}
          disabled={!product.id || removing}
          title={!product.id ? "Product identifier unavailable. Refresh the catalogue to try again." : undefined}
        >
          Remove
        </Button>
      ) : null}
    </div>
  );

  return (
    <div>
      <HKPConfirmDialog
        open={Boolean(pendingRemoval)}
        title="Remove product from catalogue?"
        description={`Remove ${pendingRemoval?.name || "this product"} from this catalogue? The product will remain in inventory.`}
        confirmLabel="Remove product"
        busy={removing}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={handleDeleteProduct}
      />
      {/* Header with Actions */}
      <div className="catalogue-tab-toolbar">
        <h3 className="catalogue-tab-heading mb-0">Products</h3>
        <div className="d-flex gap-2 flex-wrap">
          {!stockLocked ? (
            <Button
              type="button"
              outline
              className="catalogue-toolbar-btn catalogue-toolbar-btn-outline"
            >
              Bulk Upload
            </Button>
          ) : null}
          <Button
            type="button"
            outline
            className="catalogue-toolbar-btn catalogue-toolbar-btn-outline"
            disabled={savingVisiblePrices || pricingLoading || filteredProducts.length === 0}
            onClick={() => {
              const rows = filteredProducts
                .map((product) => pricingById[product.id])
                .filter((row): row is PricingSheetEntry => Boolean(row));
              void saveCataloguePrices(rows, null);
            }}
          >
            {savingVisiblePrices ? "Saving…" : "Save visible prices"}
          </Button>
          {!stockLocked ? (
            <Button
              type="button"
              className="catalogue-toolbar-btn catalogue-toolbar-btn-solid"
              onClick={handleAddFromInventory}
            >
              Add from inventory
            </Button>
          ) : (
            <span className="text-muted small align-self-center">
              Membership follows product stock automatically.
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 catalogue-products-filter-card">
        <CardBody>
          <Row className="align-items-center catalogue-products-filters g-2">
            <Col xs={12} md={3}>
              <Input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col xs={12} md={3}>
              <Input
                type="select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === "all" ? "All Categories" : cat}
                  </option>
                ))}
              </Input>
            </Col>
            <Col xs={12} md={3}>
              <Input
                type="select"
                value={
                  CATALOGUE_PRICE_PRESETS.includes(clientTier as (typeof CATALOGUE_PRICE_PRESETS)[number])
                    ? clientTier
                    : clientTier
                }
                onChange={(e) =>
                  onPriceTierChange({
                    ...priceTier,
                    mode: "preset",
                    preset: e.target.value,
                  })
                }
                title="Audience this catalogue price is for"
              >
                {CATALOGUE_PRICE_PRESETS.map((tier) => (
                  <option key={tier} value={tier}>
                    Price for {tier}
                  </option>
                ))}
                {!CATALOGUE_PRICE_PRESETS.includes(clientTier as (typeof CATALOGUE_PRICE_PRESETS)[number]) ? (
                  <option value={clientTier}>Price for {clientTier}</option>
                ) : null}
              </Input>
            </Col>
            <Col xs={12} md={2} className="text-md-end catalogue-products-count">
              <span className="text-muted small">
                {pricingLoading ? "Loading…" : `${filteredProducts.length} of ${products.length}`}
              </span>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Products Table */}
      <Card className="catalogue-products-table">
        <CardBody className="p-0">
          <div className="catalogue-data-scroll">
            <div className="catalogue-data-scroll-inner">
          {/* Table Header */}
          <div 
            className="px-3 py-2 border-bottom"
            style={{ backgroundColor: "#f8f9fa" }}
          >
            <Row className="fw-semibold text-muted small flex-nowrap">
              <Col xs={3}>Product</Col>
              <Col xs={2}>Shopper activity</Col>
              <Col xs={3}>Catalogue price · {clientTier}</Col>
              <Col xs={1}>Stock</Col>
              <Col xs={1}>Status</Col>
              <Col xs={2} className="text-end">Actions</Col>
            </Row>
          </div>

          {/* Table Body */}
          <div>
            {catalogueProductsPagination.isLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading products...</p>
              </div>
            ) : productsError ? (
              <div className="text-center py-4" role="alert">
                <p>We couldn't load the latest catalogue products. Refresh this list to try again.</p>
                <Button type="button" outline onClick={() => loadProducts()}>Try again</Button>
              </div>
            ) : (
              filteredProducts.map((product) => (
              <div key={product.id} className="px-3 py-3 border-bottom">
                <Row className="align-items-center flex-nowrap">
                  <Col xs={3}>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="catalogue-product-thumb-btn"
                        onClick={() => handleViewProduct(product)}
                        aria-label={`View ${product.name}`}
                      >
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={String(product.name || "Product")}
                          />
                        ) : (
                          <span>No image</span>
                        )}
                      </button>
                      <div className="catalogue-product-copy">
                        <h6 className="mb-1 fw-bold text-truncate">{String(product.name || "Not Available")}</h6>
                        <div className="small text-muted text-truncate">
                          SKU: {String(product.sku || "N/A")} • {String(product.category || "Not Available")}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col xs={2}>{renderShopperInterest(String(product.id))}</Col>
                  <Col xs={3}>
                    {renderProductPrice(product)}
                  </Col>
                  <Col xs={1}>
                    <span className="fw-bold">
                      {product.stock !== undefined && product.stock !== null && typeof product.stock === 'number' ? product.stock : "N/A"}
                    </span>
                  </Col>
                  <Col xs={1}>
                    {getStatusBadge(String(product.status || "out_of_stock"))}
                  </Col>
                  <Col xs={2} className="text-end">
                    {renderProductActions(product)}
                  </Col>
                </Row>
              </div>
            ))
            )}
          </div>
            </div>
          </div>

          {filteredProducts.length === 0 && !catalogueProductsPagination.isLoading && !productsError && (
            <div className="text-center py-5">
              <p className="text-muted">
                {stockLocked
                  ? catalogue.catalogueKind === "STOCK_OUT"
                    ? "No out-of-stock products yet. Pieces move here when they go out of stock."
                    : "No in-stock products yet. Published pieces appear here automatically."
                  : "No products found matching your criteria."}
              </p>
            </div>
          )}
          
          {!productsError && catalogueProductsPagination.pagination.totalPages > 1 ? (
            <div className="catalogue-products-pager">
              <span className="catalogue-products-pager-range">
                {`${catalogueProductsPagination.pagination.fromVal || 0}–${catalogueProductsPagination.pagination.toVal || 0} of ${catalogueProductsPagination.pagination.totalCount || 0}`}
              </span>
              <Pagination
                size="small"
                shape="rounded"
                count={catalogueProductsPagination.pagination.totalPages}
                color="primary"
                page={Math.min(
                  Math.max(catalogueProductsPagination.pagination.currentPage || 1, 1),
                  catalogueProductsPagination.pagination.totalPages
                )}
                onChange={(event, value) => {
                  catalogueProductsPagination.handlePageChange(event, value);
                  loadProducts(value - 1);
                }}
                siblingCount={0}
                boundaryCount={1}
              />
              <PageJumpControl
                currentPage={Math.min(
                  Math.max(catalogueProductsPagination.pagination.currentPage || 1, 1),
                  catalogueProductsPagination.pagination.totalPages
                )}
                totalPages={catalogueProductsPagination.pagination.totalPages}
                onPageJump={(_: any, value: number) => {
                  catalogueProductsPagination.handlePageChange(_, value);
                  loadProducts(value - 1);
                }}
              />
            </div>
          ) : null}
        </CardBody>
      </Card>

      {/* Product details stay in context. */}
      <HKPDrawer open={showViewModal} mode="view" title="Product Preview"
        onClose={() => setShowViewModal(false)}
        footer={<>
          <Button outline onClick={() => setShowViewModal(false)}>Close</Button>
          {viewingProduct && <Button color="primary" onClick={() => {
            setShowViewModal(false);
            handleEditProduct(viewingProduct);
          }}>Edit product</Button>}
        </>}>
          {viewingProduct ? (
            <Row>
              <Col md={4} className="text-center mb-3 mb-md-0">
                <div
                  style={{
                    width: "100%",
                    maxWidth: "240px",
                    aspectRatio: "1 / 1",
                    borderRadius: "8px",
                    border: "1px solid #e9ecef",
                    overflow: "hidden",
                    margin: "0 auto",
                    backgroundColor: "#f8f9fa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  {viewingProduct.images?.[0] ? (
                    <img
                      src={viewingProduct.images[0]}
                      alt={String(viewingProduct.name || "Product")}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span className="text-muted">No image available</span>
                  )}
                </div>
              </Col>
              <Col md={8}>
                <h5 className="fw-bold mb-2">{String(viewingProduct.name || "Not Available")}</h5>
                <div className="small text-muted mb-3">
                  SKU: {String(viewingProduct.sku || "N/A")} • Category: {String(viewingProduct.category || "Not Available")}
                </div>

                <Row className="g-3">
                  <Col sm={6}>
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small mb-1">Price</div>
                      <div className="fw-bold fs-5">
                        {viewingProduct.price > 0 ? `₹${viewingProduct.price.toLocaleString()}` : "N/A"}
                      </div>
                      {viewingProduct.basePrice > 0 && viewingProduct.basePrice !== viewingProduct.price && (
                        <div className="small text-muted">
                          Base: ₹{viewingProduct.basePrice.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small mb-1">Stock</div>
                      <div className="fw-bold fs-5">
                        {typeof viewingProduct.stock === "number" ? viewingProduct.stock : "N/A"}
                      </div>
                    </div>
                  </Col>
                  <Col sm={12}>
                    <div className="border rounded p-3">
                      <div className="text-muted small mb-2">Availability</div>
                      {getStatusBadge(String(viewingProduct.status || "out_of_stock"))}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          ) : (
            <p className="text-muted mb-0">No product selected.</p>
          )}
      </HKPDrawer>

      {/* Edit uses the same contextual drawer shell. */}
      <HKPDrawer open={showEditModal} mode="edit" title={`Edit Product: ${editingProduct?.name || ""}`}
        busy={isSavingEdit}
        onClose={() => {
          if (editStockValue !== editingProduct?.stock && !window.confirm("Discard your unsaved stock change?")) return;
          setShowEditModal(false);
        }}
        footer={<>
          <Button outline disabled={isSavingEdit} onClick={() => {
            if (editStockValue !== editingProduct?.stock && !window.confirm("Discard your unsaved stock change?")) return;
            setShowEditModal(false);
          }}>Cancel</Button>
          <Button color="primary" onClick={handleSaveProductEdit} disabled={isSavingEdit} aria-busy={isSavingEdit}>
            {isSavingEdit ? "Updating..." : "Update Product"}
          </Button>
          {draftPendingPublishId ? (
            <Button color="success" disabled={isSavingEdit} onClick={async () => {
              setIsSavingEdit(true);
              try {
                const res = await publishProductDraft(draftPendingPublishId);
                if (res?.success) {
                  notifications({ title: "Published", message: "Published changes", type: "success" });
                  setDraftPendingPublishId("");
                  setShowEditModal(false);
                  setEditingProduct(null);
                  loadProducts();
                } else {
                  notifications({ title: "Couldn't publish", message: "Your changes weren't published. Try again.", type: "danger" });
                }
              } catch {
                notifications({ title: "Couldn't publish", message: "Your changes weren't published. Try again.", type: "danger" });
              } finally { setIsSavingEdit(false); }
            }}>Publish Changes</Button>
          ) : null}
        </>}>
          {editingProduct && (
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Product Name</label>
                  <Input type="text" defaultValue={editingProduct.name} />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">SKU</label>
                  <Input type="text" defaultValue={editingProduct.sku} />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Category</label>
                  <Input type="select" defaultValue={editingProduct.category}>
                    <option>Sarees</option>
                    <option>Dupattas</option>
                    <option>Fabrics</option>
                    <option>Lehengas</option>
                  </Input>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Price (₹)</label>
                  <Input type="number" defaultValue={editingProduct.price} />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Stock Quantity</label>
                  <Input
                    type="number"
                    min={0}
                    value={editStockValue}
                    onChange={(e) => setEditStockValue(Math.max(0, Number(e.target.value || 0)))}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <Input type="select" value={editStockValue > 0 ? "in_stock" : "out_of_stock"} disabled>
                    <option value="in_stock">In Stock (derived from stock)</option>
                    <option value="out_of_stock">Out of Stock (derived from stock)</option>
                  </Input>
                </div>
              </Col>
            </Row>
          )}
      </HKPDrawer>
    </div>
  );
};

export default ProductsTab;
